import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useCalibration } from '../../services/queries/useCalibration';
import { useThemeRegistry, registryToMap } from '../../services/api/queries/useThemeTags';
import { translateTheme } from '../../utils/translationHelpers';

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)}%`;

export function CalibrationScreen() {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const registryQuery = useThemeRegistry();
  const themeRegistryMap = registryToMap(registryQuery.data);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { data, isLoading } = useCalibration();

  if (isLoading) return <ActivityIndicator color={theme.colors.primary} style={{ flex: 1 }} />;
  const g = data?.global;
  const coldStart = !g || g.status === 'insufficient' || g.posterior === null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: theme.spacing.lg }}>
      {coldStart ? (
        <Text style={styles.coldStart}>{t('calibration.coldStart')}</Text>
      ) : (
        <>
          {data!.any_cap_active && (
            <Text style={styles.capBanner}>{t('calibration.capActive')}</Text>
          )}
          <View style={styles.card}>
            <Text style={styles.statValue}>{pct(g!.win_rate)}</Text>
            <Text style={styles.statLabel}>
              {t('calibration.globalWinRate')} · {t('calibration.sample', { n: g!.n })}
            </Text>
            <Text style={styles.statSub}>
              {t('calibration.posterior')}: {pct(g!.posterior)}
            </Text>
          </View>
          <Text style={styles.section}>{t('calibration.byTheme')}</Text>
          {Object.entries(data!.themes).map(([name, band]) => (
            <View key={name} style={styles.themeRow}>
              <Text style={styles.themeName}>
                {translateTheme(name, themeRegistryMap, i18n.language)}
                {band.source === 'global' ? ` ${t('calibration.themeFallback')}` : ''}
              </Text>
              <Text style={styles.themeVal}>
                {pct(band.posterior)} · n={band.n}
              </Text>
            </View>
          ))}
        </>
      )}
      <Text style={styles.disclaimer}>{t('common.advisoryDisclaimer')}</Text>
    </ScrollView>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background },
    coldStart: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeMd,
      textAlign: 'center',
      marginTop: theme.spacing['3xl'],
    },
    capBanner: {
      backgroundColor: theme.colors.warningBackground,
      color: theme.colors.warningText,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeSm,
      marginBottom: theme.spacing.md,
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    statValue: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightBold,
    },
    statLabel: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.xs,
    },
    statSub: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.xs,
    },
    section: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing.xl,
      marginBottom: theme.spacing.sm,
    },
    themeRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.divider,
    },
    themeName: { color: theme.colors.text, fontSize: theme.typography.fontSizeMd },
    themeVal: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeMd },
    disclaimer: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'center',
      marginTop: theme.spacing.xl,
    },
  });
}
